import { inject, Injectable } from '@angular/core';
import { ViewConfig } from '../../../models/view-config.interface';
import { ConsigneeDeliveryNotesPaginatedService } from '@app/consignee/services/consignee-delivery-notes-paginated.service';
import { ConsigneeFoldersPaginatedService } from '@app/consignee/services/consignee-folders-paginated.service';
import { ConsigneeToursPaginatedService } from '@app/consignee/services/consignee-tours-paginated.service';
import { ConsigneeBundleGroupsPaginatedService } from '@app/consignee/services/consignee-bundle-groups-paginated.service';
import { EventsPaginatedService } from '@app/shared/services/events-paginated.service';
import { DeliveryNoteBundlesService } from 'api/services';
import { ToursV3Service } from 'api/addons-v3/services';
import { ConsigneeViewActionsService } from '../consignee-view-actions.service';
import { AppStore } from '@app/stores/appStore';
import { SearchStore } from '@app/stores/searchStore';
import { EventsStore } from '@app/stores/eventsStore';
import { SelectionStore } from '@app/stores/selectionStore';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { ToastService } from '@app/core/services/toast.service';
import { TranslateService } from '@ngx-translate/core';

// Import individual config factories
import { ConsigneeFoldersConfig } from './folders-config';
import { ConsigneeDeliveryNotesConfig } from './deliverynotes-config';
import { ConsigneeToursConfig } from './tours-config';
import { ConsigneeNotificationsConfig } from './notifications-config';
import { ConsigneeEcmrConfig } from './ecmr-config';
import { ConsigneeBundleGroupsConfig } from './bundlegroups-config';

/**
 * Consignee View Configuration Index
 * Aggregates all consignee-specific view configurations
 */
@Injectable({
    providedIn: 'root',
})
export class ConsigneeViewConfigService {
    private appStore = inject(AppStore);
    private searchStore = inject(SearchStore);
    private eventsStore = inject(EventsStore);
    private selectionStore = inject(SelectionStore);
    private consigneeBundleGroupsPaginatedService = inject(
        ConsigneeBundleGroupsPaginatedService
    );
    private toastService = inject(ToastService);
    private translateService = inject(TranslateService);

    constructor(
        private consigneeDeliveryNotesPaginatedService: ConsigneeDeliveryNotesPaginatedService,
        private consigneeFoldersPaginatedService: ConsigneeFoldersPaginatedService,
        private consigneeToursPaginatedService: ConsigneeToursPaginatedService,
        private eventsPaginatedService: EventsPaginatedService,
        private deliveryNoteBundlesService: DeliveryNoteBundlesService,
        private toursV3Service: ToursV3Service,
        private consigneeViewActionsService: ConsigneeViewActionsService,
        private router: Router,
        private dialog: MatDialog
    ) {}

    getViewConfigs(): Map<string, ViewConfig> {
        const configs = new Map<string, ViewConfig>();

        // Folders configuration
        configs.set(
            'consignee-folders',
            ConsigneeFoldersConfig.create({
                consigneeFoldersPaginatedService:
                    this.consigneeFoldersPaginatedService,
                deliveryNoteBundlesService: this.deliveryNoteBundlesService,
                actionsService: this.consigneeViewActionsService,
                appStore: this.appStore,
                selectionStore: this.selectionStore,
            })
        );

        // Tours configuration
        configs.set(
            'consignee-tours',
            ConsigneeToursConfig.create({
                consigneeToursPaginatedService:
                    this.consigneeToursPaginatedService,
                toursV3Service: this.toursV3Service,
                actionsService: this.consigneeViewActionsService,
                appStore: this.appStore,
                selectionStore: this.selectionStore,
            })
        );

        // Delivery Notes configuration
        configs.set(
            'consignee-deliverynotes',
            ConsigneeDeliveryNotesConfig.create({
                consigneeDeliveryNotesPaginatedService:
                    this.consigneeDeliveryNotesPaginatedService,
                actionsService: this.consigneeViewActionsService,
                appStore: this.appStore,
            })
        );

        // Bundle Groups (Mappengruppen) configuration
        configs.set(
            'consignee-bundlegroups',
            ConsigneeBundleGroupsConfig.create({
                consigneeBundleGroupsPaginatedService:
                    this.consigneeBundleGroupsPaginatedService,
                deliveryNoteBundlesService: this.deliveryNoteBundlesService,
                appStore: this.appStore,
                actionsService: this.consigneeViewActionsService,
                selectionStore: this.selectionStore,
                toastService: this.toastService,
                translateService: this.translateService,
            })
        );

        // Notifications configuration
        configs.set(
            'consignee-notifications',
            ConsigneeNotificationsConfig.create({
                eventsPaginatedService: this.eventsPaginatedService,
                actionsService: this.consigneeViewActionsService,
                appStore: this.appStore,
                eventsStore: this.eventsStore,
            })
        );

        // ecmr
        configs.set(
            'consignee-ecmr',
            ConsigneeEcmrConfig.create({
                router: this.router,
                dialog: this.dialog,
                appStore: this.appStore,
                searchStore: this.searchStore,
            })
        );

        return configs;
    }
}
